/**
 * Reactive `prefers-reduced-motion` view. Singleton, updated automatically
 * via window.matchMedia change events.
 *
 * Two consumption shapes:
 *   - `reducedMotion.value` (boolean getter) — for one-shot reads at
 *     scene init or SFX-gate time.
 *   - `reducedMotion.subscribe(cb)` → unsubscribe — for long-lived tweens
 *     that need to collapse mid-animation if the OS setting toggles.
 *
 * Defensive feature-detect: Safari < 14 doesn't have addEventListener on
 * MediaQueryList; it uses the deprecated addListener API. We fall back
 * to that when addEventListener is absent. Defensive path (h) of the
 * Pokédex design doc.
 *
 * In SSR / jsdom-free test envs (where window.matchMedia is absent),
 * value stays at the default (false) and subscribe() is a no-op-on-fire
 * (callbacks still register but nothing ever triggers them). Tests that
 * need to exercise the listener path drive `createReducedMotionStore`
 * with a mock MediaQueryShim directly.
 */

import { getReduceAnimations } from '../state/motionPrefs';

type Subscriber = (value: boolean) => void;

export interface MotionStore {
  readonly value: boolean;
  subscribe(cb: Subscriber): () => void;
}

/** Subset of MediaQueryList that this module actually uses. The DOM type
 *  is more elaborate; this shape covers what we need and lets tests
 *  inject a minimal mock. */
export interface MediaQueryShim {
  matches: boolean;
  addEventListener?: (type: 'change', handler: (e: { matches: boolean }) => void) => void;
  addListener?: (handler: (e: { matches: boolean }) => void) => void;
}

/**
 * Construct a MotionStore from a MediaQueryShim (or null). Exported so
 * tests can drive the listener wiring with a mock without depending on
 * a real browser. The module-level `reducedMotion` singleton calls this
 * with the live window.matchMedia query result.
 */
export function createReducedMotionStore(mql: MediaQueryShim | null): MotionStore {
  const subscribers = new Set<Subscriber>();
  let current = mql?.matches ?? false;

  if (mql) {
    const handler = (e: { matches: boolean }): void => {
      current = e.matches;
      for (const cb of subscribers) cb(current);
    };
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handler);
    } else if (typeof mql.addListener === 'function') {
      // Deprecated path for Safari < 14. addListener is the older
      // (deprecated, but still functional) MQL API; addEventListener
      // arrived in Safari 14.
      mql.addListener(handler);
    }
  }

  return {
    get value(): boolean {
      return current;
    },
    subscribe(cb): () => void {
      subscribers.add(cb);
      return (): void => {
        subscribers.delete(cb);
      };
    },
  };
}

function defaultMql(): MediaQueryShim | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)') as MediaQueryShim;
}

/** Singleton — initialized at module load.
 *
 *  POLICY CHANGE (user decision, playtest round 2, 2026-08-02): game
 *  code must NOT read this for effect gating anymore. The game shows
 *  full animations to everyone regardless of the OS setting; the only
 *  reduction path is the in-app Settings toggle, read via
 *  `effectsReduced()` below. This store remains for tests and any
 *  future diagnostics, not for behavior. */
export const reducedMotion: MotionStore = createReducedMotionStore(defaultMql());

/** THE effect-gating read. Every animation/FX fallback in the game keys
 *  off this — never off `reducedMotion` and never off a CSS media query
 *  (tokens.css reduced-motion rules are scoped under body.motion-reduced,
 *  which syncMotionBodyClass stamps from this same value). */
export function effectsReduced(): boolean {
  return getReduceAnimations();
}

/** Stamp the body class the CSS side keys off. Called at boot and by
 *  the Settings toggle on every change. */
export function syncMotionBodyClass(): void {
  if (typeof document === 'undefined' || !document.body) return;
  document.body.classList.toggle('motion-reduced', effectsReduced());
}
